import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Users2, Wallet, HandCoins, X, UserPlus, History, ChevronRight } from 'lucide-react';
import {
  useAccountingBundle,
  addEmployee,
  updateEmployee,
  giveAdvance,
  paySalary,
  salaryBalance,
  MONTHS,
  type Employee,
  type PaymentMethod,
} from '../lib/accountingStore';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, filterByRange, formatRangeLabel } from '../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { DashboardCard } from './dashboard/DashboardCard';
import { useCanWrite } from '../lib/permissions';
import { useTranslation } from '../lib/i18n';
import { adminEntryIso, defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../lib/workspaceAsOf';

const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

interface EmployeesProps {
  setActiveItem?: (id: string) => void;
}

export function Employees(_props: EmployeesProps) {
  const { t } = useTranslation();
  const [bundle, update] = useAccountingBundle();
  const canWrite = useCanWrite('employees');
  const dr = useDateRange('this_month');
  const [showAdd, setShowAdd] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const openEmp = bundle.employees.find((e) => e.id === openId) || null;

  const advancesInRange = useMemo(
    () => filterByRange(bundle.advances, dr.range, (a) => a.advanceDate),
    [bundle.advances, dr.range]
  );
  const salariesInRange = useMemo(
    () => filterByRange(bundle.salaryPayments, dr.range, (p) => p.paymentDate),
    [bundle.salaryPayments, dr.range]
  );

  const totalSalaryPaid = salariesInRange.reduce((s, p) => s + (p.netPay || 0), 0);
  const totalAdvances = advancesInRange.reduce((s, a) => s + (a.amount || 0), 0);
  const outstanding = bundle.employees.reduce((s, e) => s + (e.advancesTaken || 0), 0);
  const activeCount = bundle.employees.filter((e) => e.isActive).length;

  const payrollRows: ExportRow[] = salariesInRange.map((p) => ({
    Date: fmtDate(p.paymentDate),
    Employee: p.employeeName,
    Month: `${p.month} ${p.year}`,
    Basic: p.basicSalary,
    Bonus: p.bonus,
    AdvanceDeducted: p.advancesDeducted,
    Deductions: p.otherDeductions,
    NetPay: p.netPay,
    Method: p.paymentMethod,
  }));

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h2 className="app-page-title">{t('employees.title')}</h2>
          <p className="app-page-sub">
            {t('employees.subtitle', { range: formatRangeLabel(dr.range) })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu title={`${t('employees.payrollReport')} (${formatRangeLabel(dr.range)})`} excelFilename="payroll.xls" rows={payrollRows} />
          {canWrite && (
            <button type="button" className="app-btn-primary" onClick={() => setShowAdd(true)}>
              <UserPlus className="mr-1.5 h-4 w-4" /> {t('employees.addEmployee')}
            </button>
          )}
        </div>
      </div>

      <div className="mb-5">
        <DateRangeFilter
          start={dr.range.start}
          end={dr.range.end}
          preset={dr.preset}
          onStart={dr.setStart}
          onEnd={dr.setEnd}
          onPreset={dr.setPreset}
          onClear={dr.clear}
        />
      </div>

      <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
        <DashboardCard title={t('employees.activeStaff')} value={String(activeCount)} icon={Users2} accentTop="border-l-[3px] border-l-zinc-400" iconColor="text-zinc-600" onClick={() => {}} />
        <DashboardCard title={t('employees.salaryPaid')} value={inr(totalSalaryPaid)} icon={Wallet} accentTop="border-l-[3px] border-l-emerald-600" iconColor="text-emerald-700" onClick={() => {}} />
        <DashboardCard title={t('employees.advancesGiven')} value={inr(totalAdvances)} icon={HandCoins} accentTop="border-l-[3px] border-l-teal-600" iconColor="text-teal-700" onClick={() => {}} />
        <DashboardCard title={t('employees.outstandingAdvances')} value={inr(outstanding)} icon={HandCoins} accentTop="border-l-[3px] border-l-amber-500" iconColor="text-amber-700" onClick={() => {}} />
      </div>

      {bundle.employees.length === 0 ? (
        <div className="app-card app-card-inner text-center">
          <p className="text-sm text-zinc-500">No employees yet. Add your first staff member to start payroll.</p>
          {canWrite && (
            <button type="button" className="app-btn-primary mt-3" onClick={() => setShowAdd(true)}>
              <UserPlus className="mr-1.5 h-4 w-4" /> {t('employees.addEmployee')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {bundle.employees.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setOpenId(e.id)}
              className="app-card p-4 text-left transition hover:border-emerald-300 hover:shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700">
                    {e.name.slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-zinc-900">{e.name}</p>
                    <p className="truncate text-xs text-zinc-500">
                      {e.employeeCode}
                      {e.designation ? ` · ${e.designation}` : ''}
                    </p>
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    e.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'
                  }`}
                >
                  {e.isActive ? t('status.active') : t('status.disabled')}
                </span>
              </div>
              <div className="mt-4 space-y-2 border-t border-zinc-100 pt-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-xs font-medium text-zinc-500">{t('employees.salary')}</span>
                  <span className="min-w-0 truncate font-semibold tabular-nums text-zinc-900">{inr(e.salaryPerMonth)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-xs font-medium text-zinc-500">{t('employees.outstandingAdvances')}</span>
                  <span
                    className={`min-w-0 truncate font-semibold tabular-nums ${
                      e.advancesTaken > 0 ? 'text-amber-600' : 'text-zinc-900'
                    }`}
                  >
                    {inr(e.advancesTaken)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-xs font-medium text-zinc-500">Balance</span>
                  <span className="min-w-0 truncate font-semibold tabular-nums text-emerald-800">{inr(salaryBalance(e))}</span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-1 text-xs font-medium text-emerald-700">
                <span>Open profile</span>
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </div>
            </button>
          ))}
        </div>
      )}

      {showAdd && canWrite && <AddEmployeeModal onClose={() => setShowAdd(false)} onAdd={(input) => update((b) => addEmployee(b, input))} />}
      {openEmp && (
        <EmployeeProfileModal
          key={openEmp.id}
          employee={openEmp}
          advances={bundle.advances.filter((a) => a.employeeId === openEmp.id)}
          payments={bundle.salaryPayments.filter((p) => p.employeeId === openEmp.id)}
          onClose={() => setOpenId(null)}
          onUpdate={update}
          canWrite={canWrite}
        />
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  // Lock body scroll while the dialog is open so the page behind doesn't move.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Rendered via a portal to <body> so it can never be clipped or "merged" with
  // the app's top navbar / bottom nav (those live inside the scroll container).
  // z-[120] sits above the bottom nav (z-50), sidebar (z-60) and drawer overlay.
  const modal = (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="my-auto flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-zinc-200 bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-5 py-3">
          <h3 className="truncate pr-2 text-base font-semibold text-zinc-900">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:p-5 sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))]">{children}</div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}

function AddEmployeeModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (input: {
    name: string;
    mobile?: string;
    email?: string;
    designation?: string;
    joiningDate?: string;
    salaryPerMonth?: number;
    address?: string;
  }) => void;
}) {
  const [f, setF] = useState({
    name: '',
    mobile: '',
    email: '',
    designation: '',
    joiningDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate(),
    salaryPerMonth: '',
    address: '',
  });
  return (
    <ModalShell title="Add employee" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!f.name.trim()) return;
          onAdd({
            name: f.name,
            mobile: f.mobile,
            email: f.email,
            designation: f.designation,
            joiningDate: adminEntryIso(f.joiningDate),
            salaryPerMonth: Number(f.salaryPerMonth) || 0,
            address: f.address,
          });
          onClose();
        }}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <Field label="Full name *">
          <input className="app-field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
        </Field>
        <Field label="Mobile">
          <input className="app-field" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
        </Field>
        <Field label="Designation">
          <input className="app-field" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} />
        </Field>
        <Field label="Monthly salary (₹)">
          <input
            type="number"
            min="0"
            className="app-field"
            value={f.salaryPerMonth}
            onChange={(e) => setF({ ...f, salaryPerMonth: e.target.value })}
          />
        </Field>
        <Field label="Email">
          <input className="app-field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Joining date">
          <input type="date" className="app-field" value={f.joiningDate} onChange={(e) => setF({ ...f, joiningDate: e.target.value })} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Address">
            <input className="app-field" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          </Field>
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <button type="button" className="app-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="app-btn-primary">
            <Plus className="mr-1.5 h-4 w-4" /> Save employee
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EmployeeProfileModal({
  employee,
  advances,
  payments,
  onClose,
  onUpdate,
  canWrite = true,
}: {
  employee: Employee;
  advances: import('../lib/accountingStore').EmployeeAdvance[];
  payments: import('../lib/accountingStore').SalaryPayment[];
  onClose: () => void;
  onUpdate: (fn: (prev: import('../lib/accountingStore').AccountingBundle) => import('../lib/accountingStore').AccountingBundle) => void;
  canWrite?: boolean;
}) {
  const [tab, setTab] = useState<'advance' | 'salary' | 'history'>(canWrite ? 'salary' : 'history');
  const booksYmd = defaultAdminEntryDate() || resolveWorkspaceAsOfDate();
  const booksForPeriod = booksYmd ? new Date(`${booksYmd}T12:00:00`) : new Date();
  const [advForm, setAdvForm] = useState({
    amount: '',
    purpose: '',
    method: 'cash' as PaymentMethod,
    advanceDate: booksYmd,
  });
  const [salForm, setSalForm] = useState({
    month: MONTHS[booksForPeriod.getMonth()],
    year: booksForPeriod.getFullYear(),
    bonus: '',
    otherDeductions: '',
    method: 'cash' as PaymentMethod,
    paymentDate: booksYmd,
  });
  const [err, setErr] = useState('');

  const net = Math.max(
    0,
    (employee.salaryPerMonth || 0) + (Number(salForm.bonus) || 0) - (employee.advancesTaken || 0) - (Number(salForm.otherDeductions) || 0)
  );

  const profileTabs = (
    canWrite
      ? [
          { id: 'salary' as const, label: 'Pay salary', short: 'Pay', icon: Wallet },
          { id: 'advance' as const, label: 'Give advance', short: 'Advance', icon: HandCoins },
          { id: 'history' as const, label: 'History', short: 'History', icon: History },
        ]
      : [{ id: 'history' as const, label: 'History', short: 'History', icon: History }]
  );

  return (
    <ModalShell title={`${employee.name} · ${employee.employeeCode}`} onClose={onClose}>
      <div className="mb-4 space-y-2 sm:grid sm:grid-cols-3 sm:gap-2.5 sm:space-y-0">
        <Stat label="Salary" value={inr(employee.salaryPerMonth)} />
        <Stat label="Advance due" value={inr(employee.advancesTaken)} tone={employee.advancesTaken > 0 ? 'amber' : 'plain'} />
        <Stat label="Balance" value={inr(salaryBalance(employee))} tone="emerald" />
      </div>

      <div className="mb-4">
        <div className={`grid gap-2 ${profileTabs.length === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
          {profileTabs.map(({ id, label, short, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setTab(id);
                setErr('');
              }}
              className={`flex min-h-[3.25rem] flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2.5 text-center transition sm:min-h-0 sm:flex-row sm:justify-center sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2 ${
                tab === id
                  ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="text-[11px] font-semibold leading-tight sm:hidden">{short}</span>
              <span className="hidden text-xs font-semibold sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-xs font-medium text-zinc-600">
          <input
            type="checkbox"
            checked={employee.isActive}
            disabled={!canWrite}
            onChange={(e) => {
              if (!canWrite) return;
              onUpdate((b) => updateEmployee(b, employee.id, { isActive: e.target.checked }));
            }}
          />
          Active
        </label>
      </div>

      {err && <p className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{err}</p>}

      {tab === 'salary' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const dup = payments.some((p) => p.month === salForm.month && p.year === Number(salForm.year));
            if (dup) {
              setErr(`Salary for ${salForm.month} ${salForm.year} is already recorded.`);
              return;
            }
            if (!employee.isActive) {
              setErr('Employee is inactive.');
              return;
            }
            onUpdate((b) =>
              paySalary(b, {
                employeeId: employee.id,
                month: salForm.month,
                year: Number(salForm.year),
                bonus: Number(salForm.bonus) || 0,
                otherDeductions: Number(salForm.otherDeductions) || 0,
                paymentMethod: salForm.method,
                paymentDate: adminEntryIso(salForm.paymentDate),
              }).bundle
            );
            onClose();
          }}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <Field label="Month">
            <select className="app-field" value={salForm.month} onChange={(e) => setSalForm({ ...salForm, month: e.target.value })}>
              {MONTHS.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Year">
            <input type="number" className="app-field" value={salForm.year} onChange={(e) => setSalForm({ ...salForm, year: Number(e.target.value) })} />
          </Field>
          <Field label="Bonus (₹)">
            <input type="number" min="0" className="app-field" value={salForm.bonus} onChange={(e) => setSalForm({ ...salForm, bonus: e.target.value })} />
          </Field>
          <Field label="Other deductions (₹)">
            <input
              type="number"
              min="0"
              className="app-field"
              value={salForm.otherDeductions}
              onChange={(e) => setSalForm({ ...salForm, otherDeductions: e.target.value })}
            />
          </Field>
          <Field label="Method">
            <select className="app-field" value={salForm.method} onChange={(e) => setSalForm({ ...salForm, method: e.target.value as PaymentMethod })}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank</option>
            </select>
          </Field>
          <Field label="Payment date">
            <input
              type="date"
              className="app-field"
              value={salForm.paymentDate}
              onChange={(e) => setSalForm({ ...salForm, paymentDate: e.target.value })}
              required
            />
          </Field>
          <div className="flex items-end">
            <div className="w-full rounded-md bg-emerald-50 px-3 py-2 text-sm">
              <span className="text-emerald-700">Net pay</span>
              <span className="ml-2 font-bold text-emerald-900">{inr(net)}</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 sm:col-span-2">
            Outstanding advance of {inr(employee.advancesTaken)} will be auto-deducted and a Salary expense logged.
          </p>
          <div className="flex justify-end sm:col-span-2">
            <button type="submit" className="app-btn-primary w-full whitespace-nowrap sm:w-auto">
              Record salary payment
            </button>
          </div>
        </form>
      )}

      {tab === 'advance' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!advForm.amount) return;
            onUpdate((b) =>
              giveAdvance(b, {
                employeeId: employee.id,
                amount: Number(advForm.amount),
                purpose: advForm.purpose,
                paymentMethod: advForm.method,
                advanceDate: adminEntryIso(advForm.advanceDate),
              })
            );
            onClose();
          }}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <Field label="Amount (₹)">
            <input type="number" min="0" className="app-field" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} required />
          </Field>
          <Field label="Advance date">
            <input
              type="date"
              className="app-field"
              value={advForm.advanceDate}
              onChange={(e) => setAdvForm({ ...advForm, advanceDate: e.target.value })}
              required
            />
          </Field>
          <Field label="Method">
            <select className="app-field" value={advForm.method} onChange={(e) => setAdvForm({ ...advForm, method: e.target.value as PaymentMethod })}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank</option>
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Purpose">
              <input className="app-field" value={advForm.purpose} onChange={(e) => setAdvForm({ ...advForm, purpose: e.target.value })} />
            </Field>
          </div>
          <div className="flex justify-end sm:col-span-2">
            <button type="submit" className="app-btn-primary w-full whitespace-nowrap sm:w-auto">
              Give advance
            </button>
          </div>
        </form>
      )}

      {tab === 'history' && (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Salary payments</p>
            {payments.length === 0 ? (
              <p className="text-sm text-zinc-400">No salary payments yet.</p>
            ) : (
              <>
                <div className="space-y-2 sm:hidden">
                  {payments.map((p) => (
                    <div key={p.id} className="rounded-xl border border-zinc-200 bg-zinc-50/50 px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-zinc-900">
                          {p.month} {p.year}
                        </p>
                        <p className="font-semibold tabular-nums text-emerald-800">{inr(p.netPay)}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-zinc-500">{fmtDate(p.paymentDate)}</p>
                    </div>
                  ))}
                </div>
                <div className="app-table-wrap hidden sm:block">
                  <table className="app-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Date</th>
                        <th className="text-right">Net pay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {p.month} {p.year}
                          </td>
                          <td>{fmtDate(p.paymentDate)}</td>
                          <td className="text-right font-semibold">{inr(p.netPay)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Advances</p>
            {advances.length === 0 ? (
              <p className="text-sm text-zinc-400">No advances yet.</p>
            ) : (
              <>
                <div className="space-y-2 sm:hidden">
                  {advances.map((a) => (
                    <div key={a.id} className="rounded-xl border border-zinc-200 bg-zinc-50/50 px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-zinc-900">{a.purpose || 'Advance'}</p>
                        <p className="font-semibold tabular-nums text-amber-800">{inr(a.amount)}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-zinc-500">{fmtDate(a.advanceDate)}</p>
                    </div>
                  ))}
                </div>
                <div className="app-table-wrap hidden sm:block">
                  <table className="app-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Purpose</th>
                        <th className="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {advances.map((a) => (
                        <tr key={a.id}>
                          <td>{fmtDate(a.advanceDate)}</td>
                          <td className="text-zinc-600">{a.purpose || '—'}</td>
                          <td className="text-right font-semibold">{inr(a.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="app-label">{label}</label>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'plain',
}: {
  label: string;
  value: string;
  tone?: 'plain' | 'amber' | 'emerald';
}) {
  const toneClasses =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50'
      : tone === 'emerald'
        ? 'border-emerald-200 bg-emerald-50/80'
        : 'border-zinc-200 bg-zinc-50/80';
  const valueClasses =
    tone === 'amber' ? 'text-amber-700' : tone === 'emerald' ? 'text-emerald-800' : 'text-zinc-900';

  return (
    <div className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 sm:block sm:p-3 ${toneClasses}`}>
      <p className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 sm:text-[10px]">
        {label}
      </p>
      <p
        className={`min-w-0 truncate text-right text-base font-semibold tabular-nums leading-tight sm:mt-1 sm:text-left sm:text-lg ${valueClasses}`}
      >
        {value}
      </p>
    </div>
  );
}

export default Employees;
